import "./ApplianceSingle.scss";

import React, { useContext, useEffect, useState } from "react";
import GatsbyImageWrapper from "../GatsbyImageWrapper";
import { useTranslation } from "react-i18next";

import { ServicesContext } from "@sbi-international/boutik.gatsby.plugin.services";
import { usePageLocalization } from "@sbi-international/boutik.gatsby.plugin.multilocale";
import {
    ProductPrice,
    ProductStock,
    Rating,
} from "@sbi-international/boutik.gatsby.plugin.components";
import { popFrontURL } from "../../utils";
import { DroletButton } from "../primitives";
import {
    AllHeaterProductModel,
    AllProductsNodeModel,
    ApplianceArchivesModel,
    ProductPageTemplateInfo,
} from "../../models";

export default function ApplianceSingle(props: {
    appliance:
        | ProductPageTemplateInfo
        | ApplianceArchivesModel
        | AllHeaterProductModel
        | AllProductsNodeModel;
    reviews?: Queries.allProductReviewsNodeFragmentFragment;
    id?: string;
    onTouchStart?: (e: any) => void;
    onTouchEnd?: (e: any) => void;
    isDiscontinued?: boolean;
}) {
    const { appliance, id, onTouchStart, onTouchEnd, reviews } = props;
    const { currentLocale, navigateTranslated } = usePageLocalization();
    const [applianceLive, setAppianceLive] = useState<
        | ProductPageTemplateInfo
        | ApplianceArchivesModel
        | AllHeaterProductModel
        | AllProductsNodeModel
    >(appliance);
    const { t } = useTranslation("pages");

    const servicesContext = useContext(ServicesContext);
    const productService = servicesContext?.magento.productService;
    
    const goToSinglePage = () => {
        if (!props.isDiscontinued) {
            navigateTranslated(popFrontURL(applianceLive?.static.slug?.[currentLocale], 2));
        } else {
            navigateTranslated(`/archives/${applianceLive?.static.code?.toLowerCase()}/`);
        }
    };

    const sizeText = applianceLive?.static?.attributes?.size?.values?.[0]?.[currentLocale]
        ? "| " + applianceLive?.static?.attributes?.size?.values?.[0]?.[currentLocale]
        : "";
    const categoryAndSize =
        t(`layout:categories.${applianceLive?.static?.category?.split("/")?.[3]}`) + " " + sizeText;

    useEffect(() => {
        if (appliance && appliance?.live == null) {
            productService.operations
                .getProduct(appliance.static.code ?? "")
                .then((productLive) => {
                    if (productLive) {
                        setAppianceLive({ static: appliance.static, live: productLive } as any);
                    }
                });
        } else {
            setAppianceLive(appliance);
        }
    }, [appliance]);

    return (
        <div
            className="appliance-single"
            id={id || undefined}
            onTouchStart={(e) => onTouchStart && onTouchStart(e)}
            onTouchEnd={(e) => onTouchEnd && onTouchEnd(e)}
        >
            {"certifications" in (applianceLive?.static?.attributes ?? {}) &&
                (applianceLive?.static?.attributes as any)?.certifications &&
                verifyEPA((applianceLive?.static?.attributes as any).certifications) && (
                    <div className="epa-tag">EPA 2020</div>
                )}
            <div className="single-image" onClick={() => goToSinglePage()}>
                <GatsbyImageWrapper
                    className="product-img-wrapper"
                    fields={applianceLive?.static?.fields}
                />
            </div>
            <div className="single-content">
                <span className="single-size">{categoryAndSize}</span>
                <span className="single-title">
                    {applianceLive?.static?.title?.[currentLocale]}
                </span>
                <span className="line"></span>
                <span className="single-sku">{applianceLive?.static?.code}</span>
                <span className="single-price">
                    
                    {!props.isDiscontinued && (
                        <ProductPrice
                            currentPrice={applianceLive?.live?.currentPrice}
                            prefixClassName="appliance"
                        />
                    )}
                </span>
                <span className="availability">
                    <ProductStock
                        type="medium"
                        currentStock={applianceLive?.live?.currentStock}
                        isCustomMade={false}
                    />
                </span>
                <div className="rating">
                    {!props.isDiscontinued &&
                        reviews &&
                        reviews?.count !== null &&
                        reviews?.count !== 0 && (
                            <Rating.RatingScore
                                ratingScore={reviews?.average as number}
                                ratingCount={reviews?.count}
                            />
                        )}
                </div>
                <DroletButton
                    label={t("home.see-product") as string}
                    onClick={() => goToSinglePage()}
                />
            </div>
        </div>
    );

    function verifyEPA(certifications: {
        values: readonly ({ en_CA: string | null } | null)[] | null;
    }): boolean {
        return (
            !!certifications &&
            Array.isArray(certifications.values) &&
            certifications.values.length > 0 &&
            certifications.values[0] != null &&
            certifications.values[0]?.en_CA === "EPA-2020"
        );
    }
}
